import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveFacets } from '../../utils/helpers';
import SkipLink from '../screen_reader/SkipLink';
import { FacetDialog } from './FacetDialog';
import SearchActiveFacetList from './SearchActiveFacetList';
import { SearchListingContext } from './SearchListingContext';

type Props = {
  title?: string;
  typeOfResult?: 'products' | 'pages' | 'offers' | 'articles' | 'breeds' | 'results'; // author does not use
  onFacetRemove: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hideFilters?: boolean;
};

const typeOfResultMap = {
  results: 'resultwithcount',
  breeds: 'breedswithcount',
  products: 'productswithcount',
  articles: 'articleswithcount',
  offers: 'offerswithcount',
  pages: 'pageswithcount',
};

const SearchHeader = function ({ hideFilters = false, typeOfResult = 'results', ...props }: Props) {
  const context = useContext(SearchListingContext);

  const { t } = useTranslation();
  if (!context) {
    throw new Error(
      'Child components of SearchListing cannot be rendered outside the SearchListing component!',
    );
  }

  const { data } = context || {};
  const { pager, facets = {} } = data || {};
  const { title = t('Search'), onFacetRemove } = props;

  const resultWithCount = t(typeOfResultMap[typeOfResult], {
    count: pager?.total_items ?? 0,
    totalResultsLabel: context?.totalResultsLabel ?? '',
  });

  const activeFacets = getActiveFacets(facets);

  return (
    <div className="pds-flex pds-w-full pds-items-center pds-justify-between lg:pds-justify-start">
      {title ? (
        <h2 className="flex-shrink-0 pds-m-0 pds-w-[25%] pds-justify-self-start pds-text-title-lg">
          {title}
        </h2>
      ) : null}
      <div className="pds-flex pds-content-start pds-items-center pds-gap-4">
        {pager && (
          <span className="pds-shrink-0 pds-text-body-md pds-capitalize" aria-live="assertive">
            {resultWithCount}
          </span>
        )}
        {!hideFilters && (
          <div className="pds-flex pds-flex-wrap">
            {data?.search_results?.length && data.search_results.length > 0 && (
              <SkipLink to="#search-results" className="pds-mx-1">
                {t('Skip to results')}.
              </SkipLink>
            )}
            <SearchActiveFacetList
              className="pds-hidden lg:pds-flex"
              activeFacets={activeFacets}
              onFacetRemove={onFacetRemove}
            />
          </div>
        )}
        {!hideFilters && (
          <div className="lg:pds-hidden">
            <FacetDialog onFacetChange={onFacetRemove} />
          </div>
        )}
      </div>
    </div>
  );
};

SearchHeader.displayName = 'SearchListing.SearchHeader';

export default SearchHeader;
